import React, { useContext, useEffect, useRef, useState } from "react";
import {
  Alert,
  Avatar,
  Box,
  Button,
  ButtonBase,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  IconButton,
  Paper,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import MoreHorizIcon from "@mui/icons-material/MoreHoriz";
import PointOfSaleIcon from "@mui/icons-material/PointOfSale";
import RemoveIcon from "@mui/icons-material/Remove";
import currency from "currency.js";
import moment from "moment";
import { UserContext } from "../../context/UserContext";
import bkstApi from "../../api";
import Option, { OPTION_GRID_TILE_SX } from "../../product/component/Option";
import UserAvatar from "../../common/UserAvatar";
import KeyInDiscountEntry from "./KeyInDiscountEntry";

const CATEGORY_OPTIONS = [
  {
    value: "remove",
    label: "Remove Cash",
    icon: <RemoveIcon sx={{ fontSize: "2.25rem", color: "inherit" }} />,
  },
  {
    value: "add",
    label: "Add Cash",
    icon: <AddIcon sx={{ fontSize: "2.25rem", color: "inherit" }} />,
  },
  {
    value: "other",
    label: "Other",
    icon: <MoreHorizIcon sx={{ fontSize: "2.25rem", color: "inherit" }} />,
  },
];

const REMOVE_REASONS = [
  { value: "cash_transfer", label: "Cash Transfer" },
  { value: "tip_out", label: "Tip Out" },
  { value: "petty_cash", label: "Petty Cash" },
  { value: "correction", label: "Correction" },
  { value: "other", label: "Other" },
];

const ADD_REASONS = [
  { value: "add_change", label: "Add Change" },
  { value: "other", label: "Other" },
];

const OTHER_REASONS = [
  { value: "make_change", label: "Make Change" },
  { value: "count_drawer", label: "Count Drawer" },
  { value: "other", label: "Other" },
];

const ACTION_LABELS = {
  cash_in: "Add Cash",
  cash_out: "Remove Cash",
  cash_sale: "Cash Sale",
  tip_out: "Tip Out",
  cash_drop: "Cash Drop",
  payout: "Payout",
  no_sale: "No Sale",
};

const CATEGORY_TILE_SX = {
  ...OPTION_GRID_TILE_SX,
  minHeight: "110px",
  width: "100%",
  gap: 1,
};

const REASON_TILE_SX = {
  ...OPTION_GRID_TILE_SX,
  minHeight: "72px",
  width: "100%",
};

const COLUMN_PAPER_SX = {
  height: "100%",
  p: 2,
  display: "flex",
  flexDirection: "column",
  gap: 2,
};

function getAction(category) {
  if (category === "add") {
    return "cash_in";
  }
  if (category === "other") {
    return "no_sale";
  }
  return "cash_out";
}

function getReasons(category) {
  if (category === "add") {
    return ADD_REASONS;
  }
  if (category === "remove") {
    return REMOVE_REASONS;
  }
  if (category === "other") {
    return OTHER_REASONS;
  }
  return [];
}

function getActivityReasonLabel(reasonKey) {
  if (!reasonKey) {
    return "";
  }
  return (
    [...REMOVE_REASONS, ...ADD_REASONS, ...OTHER_REASONS].find((o) => o.value === reasonKey)
      ?.label || reasonKey
  );
}

function getNoteLabel(reasonKey) {
  if (reasonKey === "other") {
    return "Note";
  }
  return "Note (optional)";
}

function formatActivityAmount(entry) {
  if (entry.amount == null || entry.action === "no_sale") {
    return "—";
  }
  const formatted = currency(entry.amount).format();
  if (["cash_in", "cash_sale"].includes(entry.action)) {
    return `+${formatted}`;
  }
  if (["cash_out", "payout", "tip_out", "cash_drop"].includes(entry.action)) {
    return `-${formatted}`;
  }
  return formatted;
}

export default function AccessCashDrawerButton() {
  const { user } = useContext(UserContext);
  const [open, setOpen] = useState(false);
  const [category, setCategory] = useState("");
  const [reasonKey, setReasonKey] = useState("");
  const [amount, setAmount] = useState(0);
  const [note, setNote] = useState("");
  const [pending, setPending] = useState(false);
  const [success, setSuccess] = useState(false);
  const [submittedData, setSubmittedData] = useState(null);
  const [errors, setErrors] = useState([]);
  const [balance, setBalance] = useState(null);
  const [activity, setActivity] = useState([]);
  const [summaryLoading, setSummaryLoading] = useState(false);

  const noteRef = useRef(null);
  const errorRef = useRef(null);

  const printerId = user?.config?.receiptPrinterId;

  const reasonReady = Boolean(reasonKey) && (reasonKey !== "other" || note.trim().length > 0);

  const amountEnabled = category && category !== "other" && reasonReady;

  const canSubmit = !pending && category && reasonReady && (category === "other" || +amount > 0);

  const loadDrawerSummary = () => {
    setSummaryLoading(true);
    bkstApi("/my/pos/cash-balance")
      .then((res) => {
        setBalance(res.data?.balance ?? null);
        setActivity(Array.isArray(res.data?.activity) ? res.data.activity.slice(0, 8) : []);
      })
      .catch(() => {
        setBalance(null);
        setActivity([]);
      })
      .finally(() => {
        setSummaryLoading(false);
      });
  };

  useEffect(() => {
    if (!open || success) {
      return;
    }
    loadDrawerSummary();
  }, [open, success]);

  const resetForm = () => {
    setCategory("");
    setReasonKey("");
    setAmount(0);
    setNote("");
    setErrors([]);
    setSuccess(false);
    setSubmittedData(null);
  };

  const handleOpen = () => {
    if (!printerId || pending) {
      return;
    }
    resetForm();
    setOpen(true);
  };

  const handleClose = () => {
    if (pending) {
      return;
    }
    setOpen(false);
    resetForm();
  };

  const handleCategorySelect = (value) => {
    setCategory(value);
    setReasonKey("");
    setAmount(0);
    setNote("");
    setErrors([]);
  };

  const handleReasonSelect = (value) => {
    setReasonKey(value);
    setAmount(0);
    setNote("");
    setErrors([]);
    if (value === "other") {
      window.setTimeout(() => noteRef.current?.focus(), 100);
    }
  };

  const handleSubmit = () => {
    if (!printerId || !canSubmit) {
      return;
    }

    const payload = { action: getAction(category), reason: reasonKey };
    if (category !== "other") {
      payload.amount = +amount;
    }
    if (note.trim()) {
      payload.note = note.trim();
    }

    setPending(true);
    bkstApi
      .post(`/my/printer/${printerId}/open-cash-drawer`, payload)
      .then(() => {
        setSubmittedData({
          category,
          amount,
          reason: reasonKey,
          note: note.trim(),
        });
        setSuccess(true);
      })
      .catch((e) => {
        const msg = e.response?.data?.message || e.message || "Could not open cash drawer";
        setErrors([`Error: ${msg}`]);
        errorRef.current?.scrollIntoView({ behavior: "smooth" });
      })
      .finally(() => {
        setPending(false);
      });
  };

  return (
    <>
      <Tooltip>
        <Box component="span" sx={{ display: "inline-flex" }}>
          <IconButton
            aria-label="open cash drawer"
            color="secondary"
            onClick={handleOpen}
            disabled={!printerId}
          >
            <PointOfSaleIcon />
          </IconButton>
        </Box>
      </Tooltip>

      <Dialog fullScreen open={open} scroll="paper" onClose={handleClose}>
        {success ? (
          <DialogContent>
            <Box py={4} sx={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
              <CheckCircleIcon sx={{ fontSize: "5rem", color: "#16a085" }} />
              <Typography variant="h5" color="textSecondary">
                Cash Drawer Opened
              </Typography>
              {submittedData && (
                <Typography
                  variant="body1"
                  color="textSecondary"
                  sx={{ mt: 1, textAlign: "center" }}
                >
                  {[
                    CATEGORY_OPTIONS.find((o) => o.value === submittedData.category)?.label,
                    getActivityReasonLabel(submittedData.reason),
                    submittedData.category !== "other" && submittedData.amount
                      ? currency(submittedData.amount).format()
                      : null,
                    submittedData.note || null,
                  ]
                    .filter(Boolean)
                    .join(" | ")}
                </Typography>
              )}
              <Box mt={4} sx={{ width: "330px" }}>
                <Button
                  fullWidth
                  size="large"
                  color="primary"
                  variant="contained"
                  onClick={handleClose}
                >
                  Continue
                </Button>
              </Box>
            </Box>
          </DialogContent>
        ) : (
          <>
            <DialogTitle
              sx={{
                padding: "7px 20px",
                display: "flex",
                gap: "10px",
                justifyContent: "space-between",
                alignItems: "center",
                margin: 0,
                backgroundColor: "#DEDEDE",
                borderBottom: "3px solid #DEDEDE",
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 1, minWidth: 0, flex: 1 }}>
                <IconButton aria-label="back" onClick={handleClose} disabled={pending}>
                  <ArrowBackIcon />
                </IconButton>
                {user?.uid && (
                  <Avatar
                    src={`https://storage.googleapis.com/bakestreet-f55a0.appspot.com/img/u/${user.uid}.webp`}
                    sx={{ width: 40, height: 40 }}
                  />
                )}
                <Typography variant="h5" noWrap>
                  Manage Cash Drawer
                </Typography>
              </Box>
              <Button
                color="primary"
                variant="contained"
                onClick={handleSubmit}
                disabled={!canSubmit}
              >
                {pending ? <CircularProgress color="inherit" size={22} /> : "Open Cash Drawer"}
              </Button>
            </DialogTitle>
            <DialogContent sx={{ backgroundColor: "#F7F7F7" }}>
              <Box py={3} px={{ xs: 0, md: 1 }}>
                <Box ref={errorRef}>
                  {errors.length > 0 && (
                    <Alert sx={{ justifyContent: "center", mb: 2 }} severity="error">
                      {errors.join(". ")}
                    </Alert>
                  )}
                </Box>

                <Grid container spacing={2} alignItems="stretch">
                  <Grid item xs={12} md={3} sx={{ display: "flex", flexDirection: "column" }}>
                    <Typography variant="overline" color="textSecondary">
                      Action
                    </Typography>
                    <Paper elevation={0} sx={COLUMN_PAPER_SX}>
                      <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5, flex: 1 }}>
                        {CATEGORY_OPTIONS.map((option) => {
                          const selected = category === option.value;
                          return (
                            <ButtonBase
                              key={option.value}
                              type="button"
                              onClick={() => handleCategorySelect(option.value)}
                              sx={{ width: "100%", borderRadius: "4px", overflow: "hidden" }}
                            >
                              <Paper
                                elevation={selected ? 1 : 2}
                                sx={{
                                  ...CATEGORY_TILE_SX,
                                  backgroundColor: selected ? "#DDD" : "#DEDEDE",
                                  border: selected ? "2px solid #78374a" : "none",
                                  color: selected ? "#78374a" : "#000",
                                }}
                              >
                                <Box sx={{ display: "flex", color: selected ? "#78374a" : "#888" }}>
                                  {option.icon}
                                </Box>
                                <Typography
                                  variant="body1"
                                  sx={{
                                    textAlign: "center",
                                    textTransform: "uppercase",
                                    fontSize: "15px",
                                    fontWeight: 500,
                                  }}
                                >
                                  {option.label}
                                </Typography>
                              </Paper>
                            </ButtonBase>
                          );
                        })}
                      </Box>
                    </Paper>

                    <Typography variant="overline" color="textSecondary" sx={{ mt: 2 }}>
                      Recent Activity
                    </Typography>
                    <Paper elevation={0} sx={{ ...COLUMN_PAPER_SX, height: "auto", flex: 1 }}>
                      {summaryLoading ? (
                        <Box sx={{ display: "flex", justifyContent: "center", py: 2 }}>
                          <CircularProgress size={24} />
                        </Box>
                      ) : (
                        <>
                          <Box>
                            <Typography variant="caption" color="textSecondary" display="block">
                              Expected Balance
                            </Typography>
                            <Typography variant="h5" sx={{ fontWeight: 600 }}>
                              {balance == null ? "—" : currency(balance).format()}
                            </Typography>
                          </Box>
                          <Divider />
                          <Box sx={{ display: "flex", flexDirection: "column", gap: 1.25 }}>
                            <Typography variant="caption" color="textSecondary">
                              Recent Activity
                            </Typography>
                            {activity.length === 0 ? (
                              <Typography variant="body2" color="textSecondary">
                                No activity yet today
                              </Typography>
                            ) : (
                              activity.map((entry, idx) => (
                                <Box
                                  key={`${entry.ts}-${idx}`}
                                  sx={{
                                    display: "flex",
                                    justifyContent: "space-between",
                                    gap: 1,
                                    alignItems: "flex-start",
                                  }}
                                >
                                  <Box
                                    sx={{
                                      display: "flex",
                                      gap: 1,
                                      minWidth: 0,
                                      alignItems: "center",
                                    }}
                                  >
                                    {entry.uid ? (
                                      <UserAvatar
                                        uid={entry.uid}
                                        style={{ width: 28, height: 28, marginRight: 0 }}
                                      />
                                    ) : null}
                                    <Box sx={{ minWidth: 0 }}>
                                      <Typography variant="body2" noWrap>
                                        {getActivityReasonLabel(entry.reason) ||
                                          ACTION_LABELS[entry.action] ||
                                          entry.action}
                                      </Typography>
                                      <Typography
                                        variant="caption"
                                        color="textSecondary"
                                        display="block"
                                        noWrap
                                      >
                                        {entry.ts ? moment(entry.ts).format("h:mm A") : ""}
                                        {entry.note ? ` · ${entry.note}` : ""}
                                      </Typography>
                                    </Box>
                                  </Box>
                                  <Typography
                                    variant="body2"
                                    sx={{
                                      fontWeight: 600,
                                      whiteSpace: "nowrap",
                                      color: ["cash_in", "cash_sale"].includes(entry.action)
                                        ? "#2e7d32"
                                        : ["cash_out", "payout", "tip_out", "cash_drop"].includes(
                                              entry.action
                                            )
                                          ? "#c62828"
                                          : "text.primary",
                                    }}
                                  >
                                    {formatActivityAmount(entry)}
                                  </Typography>
                                </Box>
                              ))
                            )}
                          </Box>
                        </>
                      )}
                    </Paper>
                  </Grid>

                  <Grid item xs={12} md={4} sx={{ display: "flex", flexDirection: "column" }}>
                    <Typography variant="overline" color="textSecondary">
                      Reason
                    </Typography>
                    <Paper
                      elevation={0}
                      sx={{
                        ...COLUMN_PAPER_SX,
                        opacity: category ? 1 : 0.45,
                        pointerEvents: category ? "auto" : "none",
                      }}
                    >
                      {!category && (
                        <Typography variant="body2" color="textSecondary">
                          Select an action to continue
                        </Typography>
                      )}

                      {category && (
                        <>
                          <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
                            {getReasons(category).map((option) => (
                              <Option
                                key={option.value}
                                title={option.label}
                                sx={REASON_TILE_SX}
                                onClick={() => handleReasonSelect(option.value)}
                                selected={reasonKey === option.value}
                              />
                            ))}
                          </Box>
                          {reasonKey && (
                            <TextField
                              inputRef={noteRef}
                              label={getNoteLabel(reasonKey)}
                              value={note}
                              onChange={(e) => setNote(e.target.value)}
                              variant="outlined"
                              fullWidth
                              required={reasonKey === "other"}
                              multiline
                              minRows={2}
                            />
                          )}
                        </>
                      )}
                    </Paper>
                  </Grid>

                  <Grid item xs={12} md={5} sx={{ display: "flex", flexDirection: "column" }}>
                    <Typography variant="overline" color="textSecondary">
                      Amount
                    </Typography>
                    <Paper
                      elevation={0}
                      sx={{
                        ...COLUMN_PAPER_SX,
                        opacity: amountEnabled ? 1 : 0.45,
                        pointerEvents: amountEnabled ? "auto" : "none",
                      }}
                    >
                      {!amountEnabled && (
                        <Typography variant="body2" color="textSecondary">
                          {category === "other"
                            ? "No amount needed for this action"
                            : "Select a reason to enter an amount"}
                        </Typography>
                      )}
                      {amountEnabled && (
                        <Box sx={{ flex: 1, display: "flex", alignItems: "flex-start" }}>
                          <KeyInDiscountEntry amount={amount} onChange={(val) => setAmount(val)} />
                        </Box>
                      )}
                    </Paper>
                  </Grid>
                </Grid>
              </Box>
            </DialogContent>
          </>
        )}
      </Dialog>
    </>
  );
}
